import { Box, Chip, Divider, Grid, Icon, Paper, Typography } from "@material-ui/core";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import ApplicantHistoryItemDetail from "./ApplicantHistoryItemDetail";
import {
    IApplicantHistoryProps,
    useGetSizeByHistoryType
} from "../ApplicantHistoryHelper";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import EditIcon from "@material-ui/icons/Edit";
import { useAuthState } from "_ReactContext/AuthContext";
import _ from "lodash";
import useApplicantViewContext from "../../useApplicantViewContext";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            display: "flex",
            padding: theme.spacing(2),
            height: "100%",
            flexDirection: "column",
            minWidth: "200px", // Ensure consistent minimum width
            // Responsive minimum widths
            [theme.breakpoints.down("sm")]: {
                minWidth: "180px",
                padding: theme.spacing(1.5)
            },
            [theme.breakpoints.down("xs")]: {
                minWidth: "150px",
                padding: theme.spacing(1)
            }
        },
        chipCard: {
            marginRight: theme.spacing(0.5),
            marginTop: theme.spacing(0)
        },
        tags: {
            marginLeft: theme.spacing(1),
            maxWidth: 250
        },
        comment: {
            textAlign: "left",
            overflow: "auto"
        },
        divider: {
            marginTop: theme.spacing(1)
        },
        viewIcon: {
            margin: theme.spacing(0.1),
            padding: theme.spacing(0.1)
        }
    })
);

interface IApplicantHistoryTemplate {
    children: JSX.Element;
    type: CompleoShared.Common.ApplicantHistoryType;
    elementEnd?: JSX.Element;
    /** When true, shows an Edit button on entries that have a comment
     *  and were created by the current user. Only the comment is editable. */
    allowEditComment?: boolean;
}

const ApplicantHistoryTemplate = (
    props: IApplicantHistoryProps & IApplicantHistoryTemplate
) => {
    const classes = useStyles();
    const {
        historyItem,
        tLocal,
        children,
        type,
        language,
        elementEnd,
        allowEditComment,
        setModalCommentOpen
    } = props;
    const { historySizeType } = useGetSizeByHistoryType();
    const isCompact = historySizeType === "compact";

    const { user } = useAuthState();
    const [data] = useApplicantViewContext();

    const isCreator =
        !_.isEmpty(historyItem.createdByUser) &&
        historyItem.createdByUser === user;
    const hasComment = !_.isEmpty(historyItem.comment);
    const id = (historyItem.skGS1pk || "").split(":")[1];
    const showEditButton =
        !!id && allowEditComment && isCreator && hasComment && !data.readOnly;

    return (
        <Paper elevation={1} className={classes.paper}>
            <Grid container>
                <Grid item xs={isCompact ? 4 : 12}>
                    <ApplicantHistoryItemDetail
                        title={tLocal("Type")}
                        transformDBDataParam={{
                            value: tLocal(`ApplicantHistoryType_${type}`),
                            t: tLocal,
                            language: language
                        }}
                    />
                </Grid>
                <Grid item xs={isCompact ? 4 : 12}>
                    {children}
                </Grid>
                <Grid item xs={isCompact ? 4 : 12}>
                    <ApplicantHistoryItemDetail
                        title={tLocal("User")}
                        transformDBDataParam={{
                            value: historyItem.createdByUser,
                            t: tLocal,
                            language: language
                        }}
                    />
                </Grid>
            </Grid>
            <Typography variant="body2" className={classes.comment}>
                <div
                    dangerouslySetInnerHTML={createMarkup(historyItem.comment)}
                />
            </Typography>
            {elementEnd && (
                <Grid container item alignContent="center" alignItems="center">
                    {elementEnd}
                </Grid>
            )}
            {showEditButton && (
                <>
                    <Divider className={classes.divider} />
                    <Box display={"flex"} justifyContent="flex-end">
                        <ButtonIconTooltipCompleo
                            label={tLocal("COMPLEOGENERAL_Edit")}
                            onClick={() => {
                                setModalCommentOpen({
                                    open: true,
                                    editData: {
                                        commentId: id,
                                        historyData: historyItem
                                    }
                                });
                            }}
                            className={classes.viewIcon}
                        >
                            <EditIcon className={classes.viewIcon} />
                        </ButtonIconTooltipCompleo>
                    </Box>
                </>
            )}
        </Paper>
    );
};

function createMarkup(data: string) {
    return { __html: data };
}

export default ApplicantHistoryTemplate;
